import {Component, computed, inject, input, signal, Signal} from '@angular/core';
import {Router} from '@angular/router';


import {Alignment, DataChunk, Measure, Perspective} from '@selpia/shared/models';
import {DataBatchFacade} from '@selpia/data-batch/data-access';
import {AppConfigFacade} from '@selpia/app-config/data-access';
import {
  DirectionIndicatorComponent,
  DrilldownComponent,
  DrilldownTarget,
  L12mLineChartComponent,
  ListTableComponent,
  ReportButtonComponent,
} from '@selpia/data-batch/ui';
import {FormatByMeasurePipe} from '@selpia/measures/pipes';
import {SelectDataPointPipe} from '@selpia/data-batch/ui';
import {OptionsFacade} from '@selpia/options/data-access';
import {NavigationHelper} from '@selpia/data-batch/history-access';
import {BarMinichartComponent, OutsideClickDirective, SvgSpriteIcon} from '@selpia/shared/ui';
import {ArrayTools, DataBatchTools} from '@selpia/shared/utils';

@Component({
  selector: 'lib-feature-kpi-list',
  imports: [
    DirectionIndicatorComponent,
    ListTableComponent,
    DrilldownComponent,
    ReportButtonComponent,
    L12mLineChartComponent,
    SelectDataPointPipe,
    FormatByMeasurePipe,
    BarMinichartComponent,
    OutsideClickDirective,
  ],
  templateUrl: './feature-kpi-list.component.html',
  styleUrl: './feature-kpi-list.component.css',
})
export class FeatureKpiListComponent {
  readonly #dataBatchFacade = inject(DataBatchFacade);
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #optionsFacade = inject(OptionsFacade);

  readonly #router: Router = inject(Router);

  readonly tileViewPage = input.required<string>();
  readonly reportPath = input<string>();
  readonly topPerspective = input<Perspective>();

  protected readonly dataChunk: Signal<DataChunk | undefined> = computed(() => this.#dataBatchFacade.dataChunks()
    ?.at(0));
  protected readonly focusMeasures: Signal<Measure[]> = this.#appConfigFacade.focusMeasures;
  protected readonly date: Signal<Date | undefined> = this.#appConfigFacade.initialDate;

  protected readonly periodCode: Signal<string | undefined> = this.#optionsFacade.focusPeriod;

  protected readonly relativeMinMax: Signal<{ min: number, max: number } | undefined> = computed(() => {
    const dataChunk = this.dataChunk();
    const focusMeasures = this.focusMeasures();
    if (!dataChunk || !focusMeasures) {
      return undefined;
    }

    const periodCode = this.periodCode();
    const date = this.date();

    const points = ArrayTools.removeFalsy(
      focusMeasures.map(measure => DataBatchTools.getDataPointFor(dataChunk.dataPoints, {
        periodCode,
        date,
        measureCode: measure.growthMeasureRelativeCode,
      })?.value));

    return {min: Math.min(...points, 0), max: Math.max(...points, 0)};
  });

  protected readonly selectedMeasure = signal<Measure | null>(null);

  selectMeasure(measure: Measure) {
    if (measure === this.selectedMeasure()) {
      this.selectedMeasure.set(null);
    } else {
      this.selectedMeasure.set(measure);
    }
  }

  dismissSelected() {
    this.selectedMeasure.set(null);
  }

  handleDrilldown(target: DrilldownTarget, measure: Measure) {
    const perspective: Partial<Perspective> = {
      focusDimensionLevel: target.targetLevel,
      filterDimensions: target.dimensionItems,
      date: this.date(),
    };

    const options = {focusMeasure: measure, focusPeriod: this.periodCode()};

    const queryParams = {
      ...NavigationHelper.getParamsForPerspective(perspective),
      ...NavigationHelper.getParamsForOptions(options),
    };

    this.#router.navigate([`/${this.tileViewPage()}`], {queryParams})
      .then();
  }


  protected readonly SvgSpriteIcon = SvgSpriteIcon;
  protected readonly Alignment = Alignment;
}
